/* ========================================================================== *\
	IMPORTS
\* ========================================================================== */
import forEach from 'lodash/forEach';
import isDate from 'lodash/isDate';
import isFunction from 'lodash/isFunction';
import isNil from 'lodash/isNil';

import {
	removeTime
} from 'js-date-helper';



/* ========================================================================== *\
	TYPE DEFINITIONS
\* ========================================================================== */

/**
 * A method to check if the new value is different from the current value.
 *
 * @callback OptionCompare
 *
 * @param {any} newValue
 * @param {any} currentValue
 *
 * @returns {boolean} The result must be true when the values are equal;
 *          otherwise the result must be false.
 */

/**
 * @typedef {Object} OptionConfig
 *
 * @property {OptionCompare} [compare] Optional method to be used to check if
 *           the new value is different from the current value. When no method
 *           is provided the values will be compared using the
 *           strict (===) operator.
 * @property {Function} [transform] Optional method to transform the value
 *           before it is stored. When no method is provided the value will be
 *           stored as-is.
 * @property {Function} [validator] Optional method to check if the new value is
 *           valid before storing it. When no method is provided it is assumed
 *           the value is a valid value.
 */



/* ========================================================================== *\
	PRIVATE VARIABLES
\* ========================================================================== */
const
	propertyKey = {
		config: Symbol('config'),
		values: Symbol('values')
	};



/* ========================================================================== *\
	PRIVATE METHODS
\* ========================================================================== */

/**
 *
 * @param {OptionConfig} optionConfig
 * @param {*} newValue
 * @param {*} oldValue
 */
function isNewValueDifferent({ compare }, newValue, oldValue) {
	return isFunction(compare)
		? !compare(newValue, oldValue)
		: newValue !== oldValue;
}

/**
 *
 * @param {OptionConfig} optionConfig
 * @param {*} value
 */
function isValidValue({validator}, value) {
	return isFunction(validator)
		? validator(value)
		: true;
}

/**
 *
 * @param {OptionConfig} optionConfig
 * @param {*} value
 */
function transformValue({ transform }, value) {
	return isFunction(transform)
		? transform(value)
		: value;
}



/* ========================================================================== *\
	PUBLIC API
\* ========================================================================== */
class Config {
	/**
	 *
	 * @param {Object<string, OptionConfig>} options
	 */
	constructor(options) {
		this[propertyKey.config] = options;
		this[propertyKey.values] = new Map();
	}

	/**
	 * Updates the stored value for multiple options.
	 *
	 * @param {Object<string, any>} values
	 */
	setOptions(values) {
		forEach(values, (value, key) => {
			this.setOption(key, value);
		});
	}

	/**
	 * Returns the value stored for the specified option.
	 *
	 * @param {string} option The key of the option to get.
	 *
	 * @returns {any} The value stored for the requested option. When the
	 *          provided option key is unknown the result will be undefined.
	 */
	getOption(option) {
		return this[propertyKey.values].get(option);
	}

	/**
	 * Updates the stored value for the specified option.
	 *
	 * @param {string} option The key of the option to set.
	 * @param {*} value The value to assign to the option.
	 *
	 * @returns {boolean} The result will be true when the value of the option
	 *          has been updated; otherwise the result is false.
	 */
	setOption(option, value) {
		// Get the configuration for the option, when no configuration was found
		// there is nothing else which can be done.
		const
			optionConfig = this[propertyKey.config][option];
		if (isNil(optionConfig)) {
			return false;
		}

		// Make sure the new value is a valid option value and that the new
		// value is different from the current value. When either is false there
		// is no need to set the option value.
		if (
			!isValidValue(optionConfig, value) ||
			!isNewValueDifferent(optionConfig, value, this[propertyKey.values].get(option))
		) {
			return false;
		}

		// Update the option value, transform the value before storing it.
		this[propertyKey.values].set(option, transformValue(optionConfig, value));

		return true;
	}
}

/**
 *
 * @param {Date} a
 * @param {Date} b
 *
 * @returns {boolean}
 */
function isDateEqual(a, b) {
	if (!isDate(a) || !isDate(b)) {
		return false;
	}

	return removeTime(a).getTime() === removeTime(b).getTime();
}

/**
 *
 * @param {Date} a
 * @param {Date} b
 *
 * @returns {boolean}
 */
function isNullableDateEqual(a, b) {
	if (isNil(a) && isNil(b)) {
		return true;
	}

	return isDateEqual(a, b);
}


/* ========================================================================== *\
	EXPORTS
\* ========================================================================== */
export default Config;

export {
	isDateEqual,
	isNullableDateEqual
};
